import React, { useState } from "react";
import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase-config";
import { useAuthStore } from "./store";
import { Helmet } from "react-helmet-async";

import "./vendor/bootstrap/css/bootstrap.min.css";
import "./vendor/bootstrap-icons/bootstrap-icons.css";
import "./vendor/boxicons/css/boxicons.min.css";
import "./vendor/remixicon/remixicon.css";
import "./vendor/swiper/swiper-bundle.min.css";
import "./assets/style/General.css";
import "./assets/style/BackToTop.css";
import "./assets/style/Footer.css";
import "./assets/style/Sections.css";
import Header from "./Header";
import PendingSignupsBanner from "./components/PendingSignupsBanner";
import StudioSelector from "./components/StudioSelector";
import StudioSelectorDialog from "./components/StudioSelectorDialog";
import Hero from "./Hero";
import AboutUs from "./AboutUs";
import Pricing from "./Pricing";
import Offer from "./Offer";
import Contact from "./Contact";
import TimeTable from "./pages/TimeTable";
import Footer from "./Footer";
import BackToTop from "./BackToTop";
import LoginPage from "./components/Login";
import RegistrationPage from "./components/Registration";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Admin from "./Admin";
import CookiesAlert from "./CookieAlert";
import ProtectedRoute from "./components/ProtectedRoute";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Statute from "./pages/Statute";
import PayForLesson from "./pages/PayForLesson";
import MyPayments from "./pages/MyPayments";
import { useLocationStore } from "./hooks/useLocationStore";

const select = (el, all = false) => {
  el = el.trim();
  if (all) {
    return [...document.querySelectorAll(el)];
  } else {
    return document.querySelector(el);
  }
};

const changeHeaderColor = () => {
  let selectHeader = select("#header");
  if (selectHeader !== null) {
    if (window.scrollY > 100) {
      selectHeader.classList.add("header-scrolled");
    } else {
      selectHeader.classList.remove("header-scrolled");
    }
  }
};

const navbarlinksActive = () => {
  let navbarlinks = select("#navbar .scrollto", true);
  let position = window.scrollY + 200;
  navbarlinks.forEach((navbarlink) => {
    if (!navbarlink.hash) return;
    let section = select(navbarlink.hash);
    if (!section) return;
    if (
      position >= section.offsetTop &&
      position <= section.offsetTop + section.offsetHeight
    ) {
      navbarlink.classList.add("active");
    } else {
      navbarlink.classList.remove("active");
    }
  });
};

const SetScrollHanlder = (handler) => {
  useEffect(() => {
    const handleScroll = (event) => {
      handler();
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
};

const addBackToTop = () => {
  let backtotop = select(".back-to-top");
  if (backtotop) {
    if (window.scrollY > 100) {
      backtotop.classList.add("active");
    } else {
      backtotop.classList.remove("active");
    }
  }
};

const removePreloader = () => {
  let preloader = select("#preloader");
  if (preloader) {
    window.addEventListener("load", () => {
      preloader.remove();
    });
  }
};

const init = () => {
  AOS.init({
    duration: 1000,
    easing: "ease-in-out",
    once: true,
    mirror: false,
  });
  removePreloader();
};

const HomePage = () => {
  const [cookiePolicyAccepted, setCookiePolicyAccepted] = useState(
    document.cookie.includes("cookiePolicyAccepted=true")
  );
  const fetchMainLocations = useLocationStore(
    (state) => state.fetchMainLocations
  );

  useEffect(() => {
    fetchMainLocations();
  }, [fetchMainLocations]);

  function handleAccept() {
    setCookiePolicyAccepted(true);
  }
  return (
    <div>
      <Helmet>
        <title>Natchnienie Studio - Joga, Pilates, Reformer i Fitness w Żukowie</title>
        <meta name="description" content="Profesjonalne zajęcia jogi, pilatesu, pilates reformer i fitnessu w Żukowie. Małe grupy, doświadczeni instruktorzy, indywidualne podejście. Dołącz do naszej społeczności i odkryj swoją wewnętrzną siłę." />
        <link rel="canonical" href="https://www.natchnieniestudio.pl/" />
      </Helmet>
      <CookiesAlert onAccept={handleAccept} />
      <StudioSelectorDialog cookiePolicyAccepted={cookiePolicyAccepted} />
      <div className="home-page-content">
        <header id="header" className="fixed-top">
          <Header />
        </header>
        <PendingSignupsBanner />
        <Hero />
        <StudioSelector />
        <main id="main">
          <AboutUs />
          <TimeTable />
          <Pricing />
          <Offer />
          <Contact />
        </main>
        <Footer />
        <BackToTop />
        {/* TODO, add after switching to redux <Preloader/> */}
      </div>
    </div>
  );
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const HomePageWithAlert = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const alertMessage = queryParams.get("message");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (alertMessage) {
      setOpen(true);
    }
  }, [alertMessage]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <HomePage />
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity="info">
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const NotFound = () => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '2rem' }}>
      <Helmet>
        <title>404 - Nie znaleziono strony | Natchnienie Studio</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <h1 style={{ fontSize: '4rem', marginBottom: '1rem' }}>404</h1>
      <p style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>Strona, której szukasz, nie istnieje.</p>
      <a href="/" style={{ color: '#e0a96d', textDecoration: 'underline' }}>Wróć na stronę główną</a>
    </div>
  );
};

const PurchaseConfirmation = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const error = queryParams.get("error");

  useEffect(() => {
    if (error) {
      alert("An error occurred during the purchase process.");
    } else {
      alert("Pass was bought properly.");
    }
    // A completed checkout may have cleared unpaid admin-signup lessons —
    // refresh the in-app notification so the badge/banner reflect reality.
    useAuthStore.getState().fetchPendingSignups();
  }, [error]);

  return (
    <div>
      <h2>{error ? "Purchase Error" : "Purchase Successful"}</h2>
    </div>
  );
};

const App = () => {
  useEffect(() => {
    init();
  }, []);

  const setUserDetails = useAuthStore((state) => state.setUserDetails);
  const setUser = useAuthStore((state) => state.setUser);
  const clearUserDetails = useAuthStore((state) => state.clearUserDetails);
  const setAuthInitialized = useAuthStore((state) => state.setAuthInitialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userId = useAuthStore((state) => state.userId);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Store the Firebase user object
        setUser(user);
        
        // Only fetch user details if we don't already have them (or if userId doesn't match)
        // This prevents unnecessary API calls and race conditions during navigation
        const currentState = useAuthStore.getState();
        const hasValidPersistedData = currentState.isAuthenticated && 
                                       currentState.userId && 
                                       currentState.firebaseUid === user.uid;
        
        if (!hasValidPersistedData) {
          try {
            const token = await user.getIdToken();
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_ADDRESS}/user/me`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (response.ok) {
              const data = await response.json();
              setUserDetails({
                email: data.email,
                firebaseUid: data.firebase_uid,
                name: data.name,
                lastName: data.last_name,
                phone: data.phone,
                isAuthenticated: true,
                userId: data.id,
                isAdmin: data.is_admin || false,
                shouldSeeAdminPanel: data.should_see_admin_panel || false,
              });
            } else {
              // Only clear user details if the response indicates the user is truly not authenticated
              // Don't clear on temporary network errors or other issues
              if (response.status === 401) {
                clearUserDetails();
              }
            }
          } catch (error) {
            console.error("Error fetching user details:", error);
            // Don't clear user details on network errors - user might still be authenticated
          }
        }

        // Refresh the in-app unpaid admin-signup notification (account-icon
        // badge + warning banner) on every authenticated load.
        useAuthStore.getState().fetchPendingSignups();
      } else {
        // Only clear when Firebase explicitly says user is not authenticated
        clearUserDetails();
      }
      
      // Mark auth as initialized after first check
      setAuthInitialized(true);
    });

    return () => unsubscribe();
  }, [setUserDetails, setUser, clearUserDetails, setAuthInitialized, isAuthenticated, userId]);

  SetScrollHanlder(changeHeaderColor);
  SetScrollHanlder(navbarlinksActive);
  SetScrollHanlder(addBackToTop);
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute requireAdmin={true}>
                <Admin />
              </ProtectedRoute>
            } 
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegistrationPage />} />
          <Route path="/statute" element={<Statute />} />
          <Route
            path="/purchase-confirmation"
            element={<PurchaseConfirmation />}
          />
          {/* Public — opened directly from the admin-signup reminder email.
              Validates the token server-side; no Firebase auth required. */}
          <Route path="/pay-for-lesson/:token" element={<PayForLesson />} />
          {/* Authenticated in-app settlement: everything the logged-in user
              owes (future signups + past debt + cash-on-site info). */}
          <Route
            path="/moje-platnosci"
            element={
              <ProtectedRoute>
                <MyPayments />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
};

export default App;
